/**
 * Bucket Mapping
 *
 * This module maps S3 bucket names to their respective CDN folder URLs
 * for both Production and Development environments.
 *
 * It supports both simple bucket-to-CDN mappings and pattern-based mappings
 * that consider the object key path (similar to CloudFront behavior).
 */
import { CdnDomain, S3Bucket } from './cloud-infrastructure.constants';

/**
 * Pattern-based CDN mapping for a specific path pattern
 */
export interface PatternCdnMapping {
  /** The regex pattern to match against the object key (path after bucket name) */
  pattern: RegExp;
  /** The CDN domain to use for matching URLs */
  cdnUrl: CdnDomain;
}

/**
 * Bucket mapping value - either a simple CDN URL string or an object with patterns
 */
export type BucketMappingValue =
  | CdnDomain
  | {
      /** Default CDN URL for this bucket (used when no pattern matches) */
      default?: CdnDomain;
      /** Pattern-based mappings for CloudFront-style routing */
      patterns: PatternCdnMapping[];
    };

/**
 * Maps S3 bucket names to their CDN configurations
 */
export type BucketMapping = {
  [bucketName: string]: BucketMappingValue;
};

export const BUCKET_MAPPINGS: BucketMapping = {
  /**
   * Fluer Production environment mappings
   */
  [S3Bucket.FLUER_ASSETS]: CdnDomain.MEDIA_FLUER,
  [S3Bucket.FLUER_DOCUMENT]: CdnDomain.DOCS_FLUER,
  [S3Bucket.FLUER_UPLOADS]: CdnDomain.UPLOADS_FLUER,
  [S3Bucket.FLUER_VERSION]: CdnDomain.VERSION_FLUER,
  [S3Bucket.FLUER_CONTENT]: CdnDomain.CONTENT_FLUER,
  [S3Bucket.FLUER_AI_GENERATED_MEDIA]: CdnDomain.AI_MEDIA_FLUER,

  /**
   * Fluer Development environment mappings
   */
  [S3Bucket.FLUER_DEV_ASSETS]: CdnDomain.MEDIA_DEV_FLUER,
  [S3Bucket.FLUER_DEV_DOCUMENT]: CdnDomain.DOCS_DEV_FLUER,
  [S3Bucket.FLUER_DEV_UPLOADS]: CdnDomain.UPLOADS_DEV_FLUER,
  [S3Bucket.FLUER_DEV_VERSION]: CdnDomain.VERSION_DEV_FLUER,
  [S3Bucket.FLUER_DEV_CONTENT]: CdnDomain.CONTENT_DEV_FLUER,
  [S3Bucket.FLUER_DEV_AI_GENERATED_MEDIA]: CdnDomain.AI_MEDIA_DEV_FLUER,

  /**
   * Desygner assets.desygner.com bucket with pattern-based routing
   * Based on CloudFront distribution rules from url-mapping.md (static.desygner.com)
   *
   * Routes specific paths to static.desygner.com, with default fallback
   */
  [S3Bucket.ASSETS_DESYGNER]: {
    patterns: [
      // static.desygner.com patterns (Orders 0-10)
      // - /translations/* (Order 0)
      // - /assets/build/bundles/* (Order 1)
      // - /assets/build/vendor/* (Order 2)
      // - /assets/img/print/* (Order 3)
      // - /assets/img/errors/* (Order 4)
      // - /assets/prebuilt/* (Order 5)
      // - /assets/favicon/* (Order 6)
      // - /assets/logos/* (Order 7)
      // - /assets/headers/* (Order 8)
      // - /assets/config/* (Order 9)
      // - /assets/apps/* (Order 10)
      {
        pattern:
          /^(\/?(translations\/|assets\/build\/(bundles\/|vendor\/)|assets\/img\/(print\/|errors\/)|assets\/(prebuilt\/|favicon\/|logos\/|headers\/|config\/|apps\/))).*/,
        cdnUrl: CdnDomain.STATIC_DESYGNER,
      },
    ],
    default: CdnDomain.STATIC_DESYGNER,
  },

  /**
   * Inkive assets.inkive.com bucket
   * Based on CloudFront distribution rules from url-mapping.md
   *
   * This bucket is used as default for multiple CDN distributions:
   * - static.inkive.com
   * - static.webrand.com (for assets/*)
   * - static.inkive.me
   * - cdn.desygner.com
   * - cdn.builddesygner.xyz
   */
  [S3Bucket.ASSETS_INKIVE]: CdnDomain.STATIC_INKIVE_COM,

  /**
   * WeBrand assets.webrand.com bucket
   * Based on CloudFront distribution rules from url-mapping.md
   *
   * This bucket is used as default for multiple CDN distributions:
   * - static.webrand.com
   * - static.builddesygner.xyz
   * - static.qawebrand.xyz
   */
  [S3Bucket.ASSETS_WEBRAND]: CdnDomain.STATIC_WEBRAND,

  /**
   * Inkive virginia.inkive.com bucket with pattern-based routing
   * Based on CloudFront distribution rules from url-mapping.md
   *
   * This bucket routes virginia/* paths to multiple CDN distributions:
   * - static.inkive.com
   * - static.webrand.com
   * - static.desygner.com
   * - cdn.desygner.com
   * - cdn.builddesygner.xyz
   */
  [S3Bucket.VIRGINIA_INKIVE]: {
    patterns: [
      // virginia/* pattern - routes to static.desygner.com by default
      {
        pattern: /^\/?(virginia\/344\/|virginia\/1754\/|virginia\/877\/).*/,
        cdnUrl: CdnDomain.STATIC_INKIVE_COM,
      },
      // virginia/* pattern - routes to static.desygner.com by default
      {
        pattern: /^\/?(virginia\/).*/,
        cdnUrl: CdnDomain.STATIC_DESYGNER,
      },
    ],
  },

  /**
   * WeBrand virginia.webrand.com bucket with pattern-based routing
   * Based on CloudFront distribution rules from url-mapping.md
   *
   * This bucket is used by multiple CDN distributions:
   * - static.webrand.com
   * - static.inkive.com
   * - static.builddesygner.xyz
   * - static.desygner.com
   * - cdn.desygner.com
   * - cdn.builddesygner.xyz
   */
  [S3Bucket.VIRGINIA_WEBRAND]: {
    patterns: [
      // Combined patterns for static.webrand.com using OR operator
      // Pattern breakdown:
      // - virginia/company/* (Order 0)
      // - virginia/344/* (Order 1)
      // - virginia/1754/* (Order 2)
      // - virginia/877/* (Order 3)
      // - virginia/*/*/344/* (Order 4)
      // - api/* (Order 15)
      {
        pattern:
          /^\/?(virginia\/company\/|virginia\/344\/|virginia\/1754\/|virginia\/877\/|virginia\/[^/]+\/[^/]+\/344\/|api\/).*/,
        cdnUrl: CdnDomain.STATIC_WEBRAND,
      },
    ],
  },
};

/**
 * Retrieves the default CDN domain for a given S3 bucket name.
 *
 * @param bucketName - The S3 bucket name.
 * @returns The corresponding default CDN domain, or undefined if not found.
 */
export function getDefaultCdnDomain(bucketName: string): CdnDomain | undefined {
  const mapping = BUCKET_MAPPINGS[bucketName];

  if (!mapping) {
    return undefined;
  }

  if (typeof mapping === 'string') {
    return mapping as CdnDomain;
  }

  return mapping.default;
}
